import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, FileText } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/**
 * TermsGate — blocking acceptance dialog shown on first app launch.
 *
 * Why a hard gate (instead of an inline footer link): emergency platforms
 * carry real legal exposure. We want a documented, auditable click-through
 * acceptance — same pattern as banking/insurance apps in Nigeria.
 *
 * State: `terms_accepted_v1` in localStorage holds the version + timestamp
 * the user accepted. If we materially change the terms we bump the suffix
 * (`_v2`) and the gate re-shows. This is per-device by design — the legal
 * acceptance is to using the device, not just the account.
 *
 * The dialog:
 *  • is non-dismissible until the user clicks Accept,
 *  • offers a Decline button that takes them away from the app entirely,
 *  • links to the full /terms route for the long form.
 *
 * Version bumps: increment STORAGE_KEY (and the `version` string written
 * inside) whenever the Terms add material new clauses so existing users
 * are re-prompted. The legacy `terms_accepted_v1` keys are intentionally
 * left in place — they're harmless inert localStorage entries.
 *   v1 → v2 (Feb 13, 2026): added § 4.6 (photo evidence licence) + § 6.6
 *                           (weekly digest opt-in).
 *   v2 → v3 (Feb 14, 2026): removed § 7C (Google Maps subscription
 *                           retired); § 4.3 / § 4.4 / § 6 updated to drop
 *                           OpenStreetMap and the subscription product.
 *   v3 → v4 (Feb 14, 2026): live-video subprocessor swapped from Jitsi
 *                           Meet to Agora RTC; § 4 sub-processor list
 *                           updated.
 *   v4 → v5 (Jul  2, 2026): added § 7C (Scan2Call — video doorbell:
 *                           visitor GPS/IP location, camera track,
 *                           wallet ₦30/min billing, ₦100 welcome
 *                           bonus, physical QR stickers); § 4.4
 *                           extended with ipwho.is + Cloudflare +
 *                           Nominatim (restored); § 6 pricing table
 *                           + § 6.7 wallet refunds. All users must
 *                           re-consent because Scan2Call visitor
 *                           flows collect fresh categories (mic +
 *                           camera + GPS on a public page).
 */
const STORAGE_KEY = "terms_accepted_v5";

export default function TermsGate() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Public-visitor routes shouldn't see the Guard terms gate — they're
  // not a Guard user. The doorbell visitor page at /d/:qrToken is the
  // canonical example. `/demo` is a public campaign landing page too
  // (Scan2Call marketing walkthrough — no backend, no account) so we
  // skip the gate there as well.
  const isPublicVisitorRoute = pathname.startsWith("/d/") || pathname === "/demo";

  useEffect(() => {
    if (isPublicVisitorRoute) { setOpen(false); return; }
    try {
      const accepted = localStorage.getItem(STORAGE_KEY);
      if (!accepted) setOpen(true);
    } catch {
      // Private mode — show the gate anyway; the user just won't have it
      // remembered between sessions.
      setOpen(true);
    }
  }, [isPublicVisitorRoute]);

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: "v5",
        accepted_at: new Date().toISOString(),
      }));
    } catch {
      // best-effort; private-mode users just won't have it remembered
    }
    setOpen(false);
  };

  const decline = () => {
    // Send the user away. We deliberately don't navigate to a "you must
    // accept" page inside the same domain — the user has chosen not to use
    // Guard, so we honour that.
    window.location.href = "https://www.google.com";
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Cannot dismiss by clicking outside or pressing Escape — the only
        // way out is Accept or Decline.
        if (!o) return;
        setOpen(o);
      }}
    >
      <DialogContent
        className="max-w-lg rounded-sm [&>button[type=button]]:hidden"
        // disable Radix's default click-outside / escape close so the gate
        // behaves like a true blocking modal
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="terms-gate"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-sm bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-red-700" />
            </div>
            <DialogTitle className="font-heading text-xl font-black tracking-tighter">
              {t("terms.title")}
            </DialogTitle>
          </div>
          <DialogDescription className="font-body text-sm text-gray-700 leading-relaxed">
            {t("terms.intro")}
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm font-body text-gray-700 space-y-3 max-h-[40vh] overflow-y-auto pr-1">
          <p>
            <strong>{t("terms.p1_title")}</strong> {t("terms.p1_body")}
          </p>
          <p>
            <strong>{t("terms.p2_title")}</strong> {t("terms.p2_body")}
          </p>
          <p>
            <strong>{t("terms.p3_title")}</strong> {t("terms.p3_body")}
          </p>
          <p>
            <strong>{t("terms.p4_title")}</strong> {t("terms.p4_body")}
          </p>
          <p className="text-xs text-gray-500">
            {t("terms.consent_prefix")}
            <strong>{t("terms.consent_accept")}</strong>
            {t("terms.consent_suffix")}
            <Link
              to="/terms"
              className="underline text-gray-900 hover:text-red-700"
              onClick={() => setOpen(false)}
              data-testid="terms-gate-read-link"
            >
              {t("terms.terms_link")}
            </Link>
            .
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={decline}
            className="rounded-sm border-gray-300 hover:bg-gray-100"
            data-testid="terms-gate-decline"
          >
            {t("terms.decline")}
          </Button>
          <Button
            asChild
            variant="ghost"
            className="rounded-sm text-gray-700 hover:bg-gray-100"
            data-testid="terms-gate-read-full"
          >
            <Link to="/terms" onClick={() => setOpen(false)}>
              <FileText className="w-3.5 h-3.5 mr-1.5" /> {t("terms.read_full")}
            </Link>
          </Button>
          <Button
            onClick={accept}
            className="rounded-sm bg-red-600 hover:bg-red-700 text-white font-bold"
            data-testid="terms-gate-accept"
          >
            {t("terms.accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
